import React from 'react';
import { Box, Card, Typography, useTheme, IconButton } from '@mui/material';
import { 
  TrendingUp,
  AccessTime,
  Star,
  CheckCircle,
  AttachMoney,
  Groups,
  AutoAwesome,
  Speed,
  Schedule,
  Savings,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';

interface TestimonialCard {
  id: string;
  type: 'testimonial' | 'stat' | 'feature';
  icon: React.ReactNode;
  title: string;
  content: string;
  author?: string;
  metric?: string;
  highlight?: string;
}

const cards: TestimonialCard[] = [
  {
    id: '1',
    type: 'testimonial',
    icon: <Star sx={{ fontSize: 20, color: '#FFD700' }} />,
    title: 'Game Changer for Seller Leads',
    content: "Oppy has completely transformed how we handle seller leads. The response time is incredible and we're converting at 2.5x our previous rate.",
    author: 'Jackie Soto, eHomes',
    highlight: '⭐⭐⭐⭐⭐'
  },
  {
    id: '2',
    type: 'stat',
    icon: <Speed sx={{ fontSize: 20, color: '#4CAF50' }} />,
    title: '3.5 Second Response Time',
    content: 'While your competitors take minutes or hours, Oppy responds instantly to every lead, 24/7/365.',
    metric: '280% MORE CONVERSIONS',
    highlight: 'INSTANT RESPONSE'
  },
  {
    id: '3',
    type: 'feature',
    icon: <Schedule sx={{ fontSize: 20, color: '#2196F3' }} />,
    title: 'Save 15-20 Hours Per Week',
    content: 'Stop spending weekends texting leads. Oppy handles all the initial conversations and qualification for you.',
    metric: 'GET YOUR LIFE BACK',
    highlight: 'WORK-LIFE BALANCE'
  },
  {
    id: '4',
    type: 'testimonial',
    icon: <CheckCircle sx={{ fontSize: 20, color: '#4CAF50' }} />,
    title: 'Oppy Stands Out',
    content: "In the crowded AI assistant market, Oppy delivers real results. The human-in-the-loop approach ensures quality while scaling.",
    author: 'Derek Taylor, T3 Sixty',
    highlight: 'INDUSTRY LEADER'
  },
  {
    id: '5',
    type: 'stat',
    icon: <TrendingUp sx={{ fontSize: 20, color: '#FF5722' }} />,
    title: '67% of Leads Come After Hours',
    content: "Most leads arrive when you're with family or sleeping. Oppy captures and qualifies them all, ensuring you never miss an opportunity.",
    metric: '24/7 CAPTURE',
    highlight: 'ALWAYS ON'
  },
  {
    id: '6',
    type: 'testimonial',
    icon: <AutoAwesome sx={{ fontSize: 20, color: '#9C27B0' }} />,
    title: 'Better Quality Leads',
    content: "The AI qualification is incredible. We're only talking to serious buyers now, not tire kickers. It's saved us countless hours.",
    author: 'Tyler Brenner, Metro Brokers',
    highlight: 'QUALITY OVER QUANTITY'
  }
];

const TickerCarouselSidebar: React.FC = () => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = React.useState(0);
  
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % cards.length);
  };
  
  const handlePrev = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? cards.length - 1 : prev - 1
    );
  };
  
  React.useEffect(() => {
    const interval = setInterval(handleNext, 5000);
    return () => clearInterval(interval);
  }, []);
  
  const currentCard = cards[currentIndex];
  
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      {/* Title */}
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: 600,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #92D151 0%, #7AB544 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        Why Teams Love Oppy
      </Typography>
      
      {/* Card Display */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '400px',
        }}
      >
        {/* Navigation */}
        <IconButton
          onClick={handlePrev}
          sx={{
            position: 'absolute',
            left: -40,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 2,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        
        <IconButton
          onClick={handleNext}
          sx={{
            position: 'absolute',
            right: -40,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 2,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ArrowForward />
        </IconButton>
        
        {/* Card */}
        <Card
          sx={{
            p: 3,
            minHeight: '280px',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(33, 33, 33, 0.9) 0%, rgba(45, 45, 45, 0.9) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.95) 100%)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0, 0, 0, 0.5)'
              : '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.5s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            {currentCard.icon}
            <Typography variant="h6" fontWeight={600} fontSize="1rem">
              {currentCard.title}
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.6 }}
          >
            {currentCard.content}
          </Typography>
          
          {currentCard.author && (
            <Typography 
              variant="caption" 
              sx={{ 
                fontStyle: 'italic',
                color: theme.palette.primary.main,
                display: 'block',
                mb: 1
              }}
            >
              — {currentCard.author}
            </Typography>
          )}
          
          {currentCard.metric && (
            <Box
              sx={{
                mt: 'auto',
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-block',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, #92D151 0%, #7AB544 100%)',
                  color: 'white',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                }}
              >
                {currentCard.metric}
              </Typography>
            </Box>
          )}
          
          {currentCard.highlight && !currentCard.metric && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-block',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: theme.palette.action.selected,
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                {currentCard.highlight}
              </Typography>
            </Box>
          )}
        </Card>
      </Box>
      
      {/* Dots Indicator */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mt: 3,
        }}
      >
        {cards.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentIndex(index)}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: currentIndex === index 
                ? theme.palette.primary.main 
                : theme.palette.action.disabled,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TickerCarouselSidebar;